import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Tag, 
  Button, 
  Space, 
  Card, 
  Typography, 
  message, 
  Tooltip, 
  Badge,
  Spin,
  Empty,
  Input,
  Select,
  Row,
  Col
} from 'antd';
import { 
  EditOutlined, 
  DeleteOutlined, 
  BellOutlined,
  ExclamationCircleOutlined,
  SyncOutlined,
  ShoppingCartOutlined,
  FileTextOutlined,
  SearchOutlined,
  ReloadOutlined,
  MailOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { fetchPendingAlerts, updateAlert, dismissAlert } from './alertService';
import './AlertModule.css';
import AlertEditModal from '../../Components/Modals/AlertEditModal';

const { Title, Text } = Typography;

const AlertList = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('all');

  const loadAlerts = async () => {
    setLoading(true);
    try {
      const response = await fetchPendingAlerts();
      if (response.success) {
        setAlerts(response.data);
      } else {
        message.error('Failed to load alerts');
      }
    } catch (error) {
      message.error('Error fetching alerts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAlerts();
  }, []);

  const handleEdit = (record) => {
    setSelectedAlert(record);
    setModalVisible(true);
  };

  const handleModalOk = async (alertId, updateData) => {
    setUpdateLoading(true);
    try {
      const response = await updateAlert(alertId, updateData);
      if (response.success) {
        message.success('Alert scheduled successfully!');
        setModalVisible(false);
        setSelectedAlert(null);
        loadAlerts();
      } else {
        message.error(response.message || 'Failed to update alert');
      }
    } catch (error) {
      message.error('Error updating alert');
      console.error(error);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setSelectedAlert(null);
  };

  const handleDismiss = async (alertId) => {
    try {
      const response = await dismissAlert(alertId);
      if (response.success) {
        message.success('Alert dismissed');
        loadAlerts();
      } else {
        message.error(response.message || 'Failed to dismiss alert');
      }
    } catch (error) {
      message.error('Error dismissing alert');
      console.error(error);
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      expiry: <ExclamationCircleOutlined style={{ color: '#fa8c16' }} />,
      sync: <SyncOutlined style={{ color: '#f5222d' }} />,
      po: <ShoppingCartOutlined style={{ color: '#1890ff' }} />,
      so: <FileTextOutlined style={{ color: '#52c41a' }} />,
    };
    return icons[type] || <BellOutlined />;
  };

  const getTypeTag = (type) => {
    const typeConfig = {
      expiry: { color: 'orange', label: 'Expiry' },
      sync: { color: 'red', label: 'Sync Issue' },
      po: { color: 'blue', label: 'Purchase Order' },
      so: { color: 'green', label: 'Sales Order' },
    };
    const config = typeConfig[type] || { color: 'default', label: type };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 140,
      render: (type) => (
        <Space>
          {getTypeIcon(type)}
          {getTypeTag(type)}
        </Space>
      ),
      filters: [
        { text: 'Expiry', value: 'expiry' },
        { text: 'Sync Issue', value: 'sync' },
        { text: 'Purchase Order', value: 'po' },
        { text: 'Sales Order', value: 'so' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: 'Alert Details',
      dataIndex: 'description',
      key: 'description',
      render: (text, record) => (
        <div className="alert-cell-details">
          <Text strong>{record.title}</Text>
          <Text type="secondary" className="alert-desc-text">{text}</Text>
        </div>
      ),
    },
    {
      title: 'Recipients',
      dataIndex: 'recipients',
      key: 'recipients',
      width: 200,
      render: (recipients) => (
        <div className="recipients-cell">
          {recipients && recipients.length > 0 ? (
            <Space direction="vertical" size={2}>
              {recipients.slice(0, 2).map((email, index) => (
                <Tag key={index} icon={<MailOutlined />} className="recipient-tag">
                  {email.split('@')[0]}
                </Tag>
              ))}
              {recipients.length > 2 && (
                <Text type="secondary" className="more-recipients">
                  +{recipients.length - 2} more
                </Text>
              )}
            </Space>
          ) : (
            <Text type="secondary" italic>Not configured</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Schedule',
      key: 'schedule',
      width: 160,
      render: (_, record) => (
        record.scheduledDate ? (
          <Space direction="vertical" size={0}>
            <Text><ClockCircleOutlined /> {record.scheduledDate}</Text>
            <Text type="secondary">{record.scheduledTime}</Text>
          </Space>
        ) : (
          <Text type="secondary" italic>Not scheduled</Text>
        )
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Badge 
          status={status === 'pending' ? 'warning' : status === 'scheduled' ? 'processing' : 'default'} 
          text={status.charAt(0).toUpperCase() + status.slice(1)} 
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Configure Alert">
            <Button 
              type="primary" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Dismiss">
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              size="small"
              onClick={() => handleDismiss(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const filteredAlerts = alerts.filter(alert => {
    const matchesSearch = alert.description.toLowerCase().includes(searchText.toLowerCase()) ||
                         alert.title.toLowerCase().includes(searchText.toLowerCase());
    const matchesType = filterType === 'all' || alert.type === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <div>

        <Row gutter={16} className="filter-row">
          <Col flex="auto">
            <Input
              placeholder="Search alerts..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              className="search-input"
            />
          </Col>
          
          <Col>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={loadAlerts}
              loading={loading}
            >
              Refresh
            </Button>
          </Col>
        </Row>

        <Spin spinning={loading}>
          {filteredAlerts.length > 0 ? (
            <Table
              columns={columns}
              dataSource={filteredAlerts}
              rowKey="id"
              pagination={false}
              scroll={{ x: 1000 }}
              className="alerts-table"
            />
          ) : (
            <Empty 
              description="No pending alerts found" 
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Spin>

      <AlertEditModal
        visible={modalVisible}
        alert={selectedAlert}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        loading={updateLoading}
      />
    </div>
  );
};

export default AlertList;
